import { useState, Dispatch, SetStateAction, useRef, useMemo } from "react";
import { Card, Container, Row, Col, Badge, Button } from "reactstrap";
import { Popover, Whisper } from "rsuite";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BasicTableView from "../../CloudListView/BasicTableView";
import { Field, Module } from "../../../../types/TenantInterfaces";
import IconButton from "../../../../elements/IconButton";
import EditFieldDrawer from "../../../../components/Drawers/Fields/EditFieldDrawer";
import useModifyField from "../../../../api/api-hooks/modules/fields/useModifyField";
import AppPageHeader from "../../../../components/Headers/AppPageHeader";
import { AppConfirm } from "../../../../utils/PopupUtil";
import useDeleteField from "../../../../api/api-hooks/modules/fields/useDeleteField";
import TenantAPI from "../../../../api/api/TenantAPI";
import DropdownOptionWithIcon from "../../../../components/Menus/DropdownOptionWithIcon";
import PopoverDropdownMenu from "../../../../components/Menus/PopoverDropdownMenu";
import DropdownMenuDivider from "../../../../components/Menus/DropdownMenuDivider";
import EditModuleModal from "../../../../components/Modals/EditModuleModal";
import useRenameModule from "../../../../api/api-hooks/modules/useRenameModule";
import useDeleteModule from "../../../../api/api-hooks/modules/useDeleteModule";
import useToggle from "../../../../hooks/useToggle";
import DeleteModuleModal from "../../../../components/Modals/DeleteModuleModal";
import CreateFieldDrawer from "../../../../components/Drawers/Fields/CreateFieldDrawer";
import useCreateField from "../../../../api/api-hooks/modules/fields/useCreateField";
import { BreadCrumSupportal } from "./CloudTenantSchemaView";
import useCloud from "../../../../api/api-hooks/cloud/useCloud";
import AppHelmet from "../../../../components/AppComponents/AppHelmet";
import ChangeAPINameModal from "./ChangeAPINameModal";
import useCloudSchema from "../../Tools/SchemaComparison/useCloudSchema";
import Constants from "../../../../utils/Constants";

type FieldAction = "Create" | "Update" | null;
type ModuleAction = "Rename" | "Delete" | null;

const CloudModuleSchema = () => {
  const { cloudID, moduleAPIName }: any = useParams();
  const navigate = useNavigate();
  const { cloud, cloudLoading } = useCloud(cloudID);
  const [field, setField] = useState<Field | null>(null);
  const [open, setOpen] = useToggle(false);
  const [changeAPIName, setChangeAPIName] = useState(false);
  const [changeFieldAPIName, setChangeFieldAPIName] = useState("");
  const [fieldAction, setFieldAction] = useState<FieldAction>(null);
  const [moduleAction, setModuleAction] = useState<ModuleAction>(null);
  const { deleteField, deleteFieldID } = useDeleteField();
  const { modifyField, modifyingField, modifyFieldError } = useModifyField();
  const { createField, creatingField, createFieldError } = useCreateField();
  const { renameModule, renameLoading, renameError } = useRenameModule();
  const { deleteModule, deleteModuleLoading, deleteModuleError } =
    useDeleteModule();
  const tableSettingsRef = useRef<any>();
  const { cloudSchema: schema, cloudSchemaLoading } = useCloudSchema(cloudID);

  const { pathname } = useLocation();
  const previousPath = pathname.split("/").slice(0, -1).join("/");

  const module = useMemo(() => {
    if (cloudSchemaLoading || !schema) return null;
    return schema?.CRM?.Modules?.find(
      (m: Module) => m.APIName === moduleAPIName
    );
  }, [schema, cloudSchemaLoading, moduleAPIName]);

  if (cloudLoading || cloudSchemaLoading) {
    return <div>Loading...</div>;
  }

  if (!module || !schema) {
    navigate(`/supportal/clouds/${cloudID}/modules`);
    return null;
  }

  return (
    <>
      <AppHelmet title="Clouds - GrowthCloud.com" />
      <div className="header pb-6">
        <Container fluid>
          <AppPageHeader
            title={`${module?.Name} Module Settings`}
            description={`Manage your schema inside this tenant.`}
            customContent={
              <>
                <Whisper
                  placement="autoVerticalEnd"
                  trigger="click"
                  // @ts-ignore
                  ref={tableSettingsRef}
                  speaker={
                    <Popover
                      className="sidebar-popover"
                      full
                      onClick={(e: any) => {
                        e.stopPropagation();
                        tableSettingsRef.current?.close();
                      }}
                    >
                      <ModuleSettingsOptions
                        module={module}
                        loadingDelete={deleteModuleLoading}
                        loadingRename={renameLoading}
                        setOpen={setOpen}
                        setAction={setModuleAction}
                        indexesRedirect={indexesRedirect}
                        setChangeAPIName={setChangeAPIName}
                        tenantID={cloudID}
                      />
                    </Popover>
                  }
                  controlId="newRecordDropdown"
                >
                  <Button className=" btn-icon btn-outline-secondary">
                    <span className="btn-inner--text">Actions</span>
                    <span className="btn-inner--icon">
                      <i
                        className="mdi mdi-chevron-down"
                        style={{
                          position: "relative",
                          top: "1px",
                          fontSize: 16,
                        }}
                      />
                    </span>
                  </Button>
                </Whisper>
                <Button
                  color="primary"
                  outline
                  onClick={() => {
                    setOpen();
                    setFieldAction("Create");
                  }}
                >
                  Add Field
                </Button>
              </>
            }
            breadcrumbs={
              <BreadCrumSupportal
                loading={cloudLoading}
                title={cloud?.Name ?? "Cloud"}
                redirectTitle="Module"
                redirectPath={previousPath}
              />
            }
          />
        </Container>
        <Container fluid>
          <Row>
            <Col>
              <Card>
                <BasicTableView
                  loading={cloudSchemaLoading}
                  items={(module as Module).Fields ?? []}
                  emptyMessage={"No Modules Found"}
                  title="Field"
                  getItemTitle={(f) => f.Name}
                  getItemSubtitle={(f) => f.APIName}
                  CustomRenderer={(f) => {
                    return !f?.SystemField ? (
                      <div>
                        <Badge className="mr-4">{f?.FieldType}</Badge>
                        <IconButton
                          icon="mdi mdi-api"
                          tooltipText="Change API Name"
                          onClick={() => {
                            setChangeAPIName(true);
                            setChangeFieldAPIName(f?.APIName);
                          }}
                        />
                        <IconButton
                          icon="pencil"
                          tooltipText="Edit Field"
                          onClick={() => {
                            setField(f);
                            setOpen();
                            setFieldAction("Update");
                          }}
                        />
                        <IconButton
                          icon="close"
                          tooltipText="Delete Field"
                          onClick={() => {
                            onDeleteModuleField(f);
                          }}
                          color="danger"
                          loading={f?.APIName === deleteFieldID}
                        />
                      </div>
                    ) : (
                      <Badge
                        color="light"
                        style={{
                          position: "relative",
                          top: "-1px",
                        }}
                      >
                        System Field
                      </Badge>
                    );
                  }}
                />
              </Card>
            </Col>
          </Row>
        </Container>
        <ChangeAPINameModal
          open={changeAPIName}
          onClose={() => {
            setChangeAPIName(false);
            setChangeFieldAPIName("");
          }}
          module={module}
          tenantID={cloudID}
          type={changeFieldAPIName ? "Field" : "Module"}
          fieldAPIName={changeFieldAPIName}
        />
        <EditModuleModal
          open={open && moduleAction === "Rename"}
          toggle={setOpen}
          onUpdate={onRenameModule}
          submitting={renameLoading}
          error={renameError}
          existingName={module?.Name}
          onClose={() => {
            setModuleAction(null);
            setOpen();
          }}
          mode={null}
          tenantID={cloudID}
        />
        <DeleteModuleModal
          open={open && moduleAction === "Delete"}
          toggle={setOpen}
          onDelete={onDeleteModule}
          moduleName={module?.Name}
          submitting={deleteModuleLoading}
          error={deleteModuleError}
        />
        <CreateFieldDrawer
          open={open && fieldAction === "Create"}
          onClose={() => {
            setOpen();
            setFieldAction(null);
          }}
          submitting={creatingField}
          error={createFieldError}
          onSave={onCreateModuleField}
          showModificationProtection={true}
        />
        <EditFieldDrawer
          showModificationProtection={true}
          defaultProtected={true}
          open={open && fieldAction === "Update"}
          module={module}
          onClose={() => {
            setOpen();
            setField(null);
          }}
          schema={schema}
          onSave={onUpdateModuleField}
          submitting={modifyingField}
          error={modifyFieldError}
          // @ts-ignore
          field={field}
        />
      </div>
    </>
  );

  function onSuccess() {
    setOpen();
    setFieldAction(null);
    setModuleAction(null);
  }
  // Module Operations.

  function onDeleteModule() {
    if (!module) return;

    deleteModule({
      moduleAPIName: module.APIName,
      moduleName: module.Name,
      supportTargetCloudID: cloudID, // Support Request - Provide Cloud ID!
      onSuccess: onSuccess,
    });

    navigate(
      `/clouds/${Constants.SUP_CLOUD_ID}/supportal/clouds/${cloudID}/modules`
    );
  }

  function onRenameModule(values: any) {
    if (!module) return;

    renameModule({
      moduleAPIName: module?.APIName,
      moduleName: values?.Name,
      supportTargetCloudID: cloudID,
      onSuccess: onSuccess,
    });
  }

  function indexesRedirect() {
    navigate(
      `/clouds/${Constants.SUP_CLOUD_ID}/supportal/clouds/${cloudID}/modules/${moduleAPIName}/indexes`
    );
  }

  //----------- Start of Field Operations -------------

  function onUpdateModuleField(
    fieldAPIName: string,
    fieldValues: any,
    onSuccess: () => void
  ) {
    modifyField({
      moduleAPIName: moduleAPIName,
      fieldAPIName: fieldAPIName,
      fieldValues: fieldValues,
      supportTargetCloudID: cloudID, // Support Request - Provide Cloud ID!
      onSuccess: onSuccess,
    });
  }

  function onCreateModuleField(
    fieldValues: Field,
    onSuccess: () => void,
    onError: () => void
  ) {
    createField({
      moduleAPIName: module?.APIName as string,
      fieldValues,
      supportTargetCloudID: cloudID, // Support Request - Provide Cloud ID!
      onSuccess,
      onError,
    });
  }

  async function onDeleteModuleField(field: Field) {
    if (!module) return;

    if (module?.CalculationFields?.hasOwnProperty(field.APIName)) {
      return await AppConfirm({
        title: `You can't delete '${field.Name}'`,
        html: `The field ${
          field.Name
        } is being used on the following Calculation Fields: ${module.CalculationFields[
          field.APIName
        ].map((item: any) => item)}`,
        showConfirmButton: false,
        showCancelButton: false,
      });
    }

    const result = await AppConfirm({
      title: `Delete '${field.Name}' Field?`,
      html: `Are you sure you want to delete the '${field.Name}' field? <br/> This may cause inconsistencies with your records and any references associated with this module.`,
    });
    if (!result.isConfirmed) return;

    deleteField({
      moduleAPIName: module.APIName,
      fieldAPIName: field.APIName,
      supportTargetCloudID: cloudID, // Support Request - Provide Cloud ID!
    });
  }
};

const ModuleSettingsOptions = ({
  module,
  loadingRename,
  loadingDelete,
  tenantID,
  setOpen,
  setAction,
  indexesRedirect,
  setChangeAPIName,
}: {
  module: Module;
  loadingRename: boolean;
  loadingDelete: boolean;
  tenantID: string;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setAction: Dispatch<SetStateAction<ModuleAction>>;
  indexesRedirect?: () => void;
  setChangeAPIName: Dispatch<SetStateAction<boolean>>;
}) => {
  const [exporting, setExporting] = useToggle(false);
  const handleExportModule = async () => {
    setExporting();
    try {
      const data = await TenantAPI.exportModuleJson(module.APIName, tenantID);
      const donwloadURL = data.data.data;
      window.open(donwloadURL, "_blank");
    } catch (error) {
      console.error(error);
    }
    setExporting();
  };

  return (
    <PopoverDropdownMenu>
      <h5 className="px-2 pt-2 text-dark">Manage</h5>
      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name="Rename Module"
        icon="mdi mdi-pencil-outline"
        onClick={() => {
          setAction("Rename");
          setOpen(true);
        }}
        disabled={loadingRename || module?.Protected}
      />
      <DropdownOptionWithIcon
        name="Change API Name"
        icon="mdi mdi-api"
        onClick={() => setChangeAPIName(true)}
        disabled={loadingRename || module?.Protected}
      />
      <DropdownOptionWithIcon
        name="Download Module as Json"
        icon="mdi mdi-download"
        onClick={() => {
          handleExportModule();
        }}
        disabled={exporting || (module && module.Protected)}
      />
      {indexesRedirect && (
        <DropdownOptionWithIcon
          name="Manage Indexes"
          icon="mdi mdi-application-edit-outline"
          onClick={indexesRedirect}
          disabled={!indexesRedirect}
        />
      )}
      <DropdownOptionWithIcon
        name="Delete Module"
        icon="mdi mdi-delete"
        onClick={() => {
          setAction("Delete");
          setOpen(true);
        }}
        disabled={loadingDelete || (module && module.Protected)}
        actionColor="danger"
        optionColor="var(--danger)"
      />
    </PopoverDropdownMenu>
  );
};

export default CloudModuleSchema;
